import { Choices } from './Choices';
import { INavState, NavState } from './NavState';
import { IRootStore } from '../Root/RootStore';
import { ITreeNode } from '../TreeStore/TreeNode';
import { Instance, cast, flow, getRoot, types } from 'mobx-state-tree';
import { Tree } from '../TreeStore/Tree';
import { TreeNavContent } from './TreeNavContent';

interface TreeUpdate {
  promise: Promise<void>;
  /**
   * The node id that triggered this update
   */
  id: string;
  modifiesNextNode?: boolean;
  modifiesNodesAfterNext?: boolean;
  complete: boolean;
}
const TREE_RELOAD_ID = '__reload';

interface TreeReload {
  promise: Promise<void>;
  id: typeof TREE_RELOAD_ID;
  complete: boolean;
  modifiesNextNode?: undefined;
  modifiesNodesAfterNext?: undefined;
}

type TreeUpdateEntry = TreeUpdate | TreeReload;

export const TreeNav = types
  .model('TreeNav', {
    nav: types.maybe(NavState),
    tree: types.maybe(types.reference(Tree, {
      get(id, parent) {
        const root = getRoot<IRootStore>(parent);
        const tree = root.trees.getTree(id as string).data;
        if (!tree) {
          throw new Error(`Tree with id ${id} not found.`);
        }
        return tree;
      },
      set(value) {
        return value.dataRef.dataId;
      }
    })),
    choices: types.optional(Choices, {}),
    content: types.optional(TreeNavContent, {}),
  })
  .actions(self => {
    return {
      clear() {
        self.nav = undefined;
        self.tree = undefined;
        self.choices = cast({});
      },
      start(id: string) {
        const trees = getRoot<IRootStore>(self).trees;
        self.tree = trees.getTree(id).data;
        if (!self.tree) {
          throw new Error(`Tree with id ${id} not found.`);
        }
        const { nodes, edges } = self.tree;
        const roots = nodes.filter(node => !edges.some(edge => edge.next.key === node.key));
        if (roots.length > 1) {
          console.log('nodes', nodes.map(n => n.key));
          console.log('edges', edges.map(e => [e.previous, e.next]));
          throw new Error('Invalid tree: multiple root nodes. ' + roots.map(root => root.key));
        }
        if (!roots.length) {
          throw new Error('Invalid tree: no root.');
        }
        const root = roots[0];
        const nav: INavState = {
          current: root.key,
          isStart: true,
          // a single-frame tree (e.g. a one-frame activity session) is final
          // from the start: its button must read Complete, not Continue
          isFinal: self.tree.isLeaf(root.key),
          path: cast([root.key]),
        };
        self.nav = nav;
      },
      moveBack(to?: string | null) {
        const { nav } = self;
        if (!self.tree) {
          throw new Error('Tree not set.');
        }
        if (!nav) {
          throw new Error(`Cannot advance when nav is undefined (dataId=${self.tree?.dataRef.dataId}, dataType=${self.tree?.dataRef.type}).`);
        }
        if (nav.path.length === 1) {
          throw new Error('Cannot go back. Empty path. Must advance first.' + nav.current);
        }
        if (to && !nav.path.includes(to)) {
          throw new Error(`${to} is not on the nav path (${nav.path.toString()}). Cannot go back to ${to}.`);
        }
        if (!to) {
          // default to going back one step
          to = nav.path[nav.path.length - 2];
        }
        while(nav.current !== to) {
          // pop current
          nav.path.pop();
          // update current to previous
          nav.current = nav.path[nav.path.length - 1];
        }
        // final frame when no edges start here
        nav.isFinal = self.tree.isLeaf(nav.current);
        // first frame when no edges point to here
        nav.isStart = self.tree.isRoot(nav.current);
      },
      advanceTo(key?: string | null) {
        const { nav } = self;
        if (!self.tree) {
          throw new Error('Tree not set.');
        }
        if (!nav) {
          throw new Error(`Cannot advance when nav is undefined (dataId=${self.tree?.dataRef.dataId}, dataType=${self.tree?.dataRef.type}).`);
        }
        // possible next frames have edges that start on current
        const options = self.tree.getEdgesFrom(nav.current);
        if (!key) {
          key = options.find(opt => opt.default)?.next?.key;
          if (!key) {
            throw new Error('Cannot advance without key. No default option. (options: ' + options.map(opt => opt.next).join(', ') + ')');
          }
        }
        if (!options.some(opt => opt.next.key === key)) {
          throw new Error(`Cannot advance to ${key} from ${nav.current}`);
        }
        // allowed to advance
        nav.path.push(key);
        nav.current = key;
        nav.isFinal = self.tree.isLeaf(nav.current);
        nav.isStart = false;
      },
    };
  })
  // manage updates and reloads
  .extend(self => {
    const updates: TreeUpdateEntry[] = [];
    const preserveNavState = () => {
      if (!self.nav || !self.tree) {
        return null;
      }
      return {
        current: self.nav.current,
        path: [...self.nav.path],
        isStart: self.nav.isStart || false,
        isFinal: self.nav.isFinal || false,
      };
    };
    const restoreNavState = (preservedState: { current: string; path: string[]; isStart: boolean; isFinal: boolean } | null) => {
      if (!preservedState || !self.tree) {
        return;
      }

      // Filter path to only include nodes that exist in the reloaded tree
      const validPath = preservedState.path.filter(nodeKey =>
        self.tree?.findNodeByKey(nodeKey) !== undefined
      );

      // If current node doesn't exist in new tree, fall back to the last valid node in path
      let currentNode = preservedState.current;
      if (!self.tree.findNodeByKey(currentNode)) {
        if (validPath.length > 0) {
          currentNode = validPath[validPath.length - 1];
        } else {
          // No valid nodes in path, restart from tree root
          self.start(self.tree.dataRef.dataId);
          return;
        }
      }

      // Ensure current node is in the valid path
      if (!validPath.includes(currentNode)) {
        validPath.push(currentNode);
      }

      // Restore navigation state with valid nodes only
      const nav: INavState = {
        current: currentNode,
        path: cast(validPath),
        isStart: self.tree.isRoot(currentNode),
        isFinal: self.tree.isLeaf(currentNode),
      };
      self.nav = nav;
    };

    return {
      actions: {
        reloadTree: flow(function*() {
          console.log('TreeNav.reloadTree', self.tree?.dataRef.dataId);
          const trees = getRoot<IRootStore>(self).trees;
          if (!self.tree) {
            throw new Error('Tree not set.');
          }
          const dataId = self.tree.dataRef.dataId;
          const dataType = self.tree.dataRef.type;

          // Reload the tree
          yield trees.reloadTree(dataId, dataType);

          updates.length = 0; // clear updates after reload
        }),
        /**
         * Append an update to tree update queue.
         * The update steps are:
         *  1. Queue any updates that can modify the tree
         *     - this stores the id of the node that triggered the update
         *     - this stores whether the update could modify the next node or nodes after next
         *  2. When all updates are complete, this will reload the tree in the background
         *  3. When navigating forwards, check whether any updates have (or will) modify the next node (see shouldWaitForUpdates)
         *     - If the next node has (or will be) modified, wait for updates and local reload before navigating forward (see waitForUpdates)
         */
        async appendUpdate(update: Promise<void>): Promise<void> {
          if (self.nav === undefined) {
            console.warn('TreeNav.appendUpdate: nav is undefined, cannot append update');
            return Promise.resolve();
          }
          const id = self.nav.current;
          const currentNode = self.nav.current
            ? self.tree?.findNodeByKey(self.nav.current)
            : undefined;

          const updateEntry: TreeUpdate = {
            promise: update,
            id,
            modifiesNextNode: currentNode?.modifiesNextNode,
            modifiesNodesAfterNext: currentNode?.modifiesNodesAfterNext,
            complete: false,
          };

          console.log('TreeNav.appendUpdate: appending update for', id, updates);
          updates.push(updateEntry);

          try {
            await update;
          } finally {
            const index = updates.findIndex(u => u.id === id);
            if (index !== -1) {
              updates[index].complete = true;
            }
            // if all updates are complete
            if (updates.every(u => u.complete)) {
              // reload the tree in the background
              updates.push({
                promise: this.reloadTree().finally(() => {
                  // remove this reload entry after it's done
                  const reloadIndex = updates.findIndex(u => u.id === TREE_RELOAD_ID);
                  if (reloadIndex !== -1) {
                    updates.splice(reloadIndex, 1);
                  }
                  console.log('TreeNav.appendUpdate: reload complete', self.nav?.path);
                }),
                id: TREE_RELOAD_ID,
                complete: false,
              });
              console.log('TreeNav.appendUpdate: all updates complete, reloading tree', id, updates);
            }
            console.log('TreeNav.appendUpdate: update completed for', id, updates);
          }
        },
      },
      views: {
        get shouldReload(): boolean {
          if (!self.nav || updates.length === 0) {
            return false;
          }
          const currentId = self.nav.current;
          const currentNode = self.tree?.findNodeByKey(currentId);
          const previousUpdates = updates.filter(u => u.id !== currentId);
          const shouldReload = currentNode?.modifiesNextNode || (previousUpdates.length > 0 && !previousUpdates.some(u => !u.complete));
          console.log('TreeNav.shouldReload', currentId, updates.map(u => `${u.id}: ${u.complete}`), previousUpdates, `nextQuestionDependsOnSurveyResponse=${self.content.question?.nextQuestionDependsOnSurveyResponse}`, shouldReload);

          return shouldReload;
        },
        get shouldWaitForUpdates(): boolean {
          // If no current navigation or no updates pending, no need to wait
          if (!self.nav || updates.length === 0) {
            return false;
          }
          // if any updates could have modified (or will modify) the next node
          //  (1) if current node modifies next node
          //  (2) if any previous nodes could modify next node (nodeAfterNext)
          return updates.some(u =>
            (u.id === self.nav?.current && u.modifiesNextNode) ||
            (u.id !== self.nav?.current && u.modifiesNodesAfterNext)
          );
        },
        async waitForUpdates(): Promise<void> {
          if (!self.nav || updates.length === 0) {
            return;
          }
          console.log('TreeNav.waitForUpdates: waiting for updates', updates.map(u => `${u.id}: ${u.complete}`));
          // iterate through updates array
          // by iterating (instead of something like Promise.all) we allow for new updates to be appended while waiting
          // this can happen if the user navigates quicker than updates (and reloads) can complete
          for (const u of updates) {
            await u.promise;
          }
        }
      }
    };
  })
  // core nav views
  .views(self => {
    return {
      get started(): boolean {
        return self.nav !== undefined;
      },
      /**
       * True when the current node's dataRef resolves, throws otherwise
       */
      get isContentReady(): boolean {
        if (!self.tree) {
          return false;
        }
        return this.currentNode?.isContentReady || false;
      },
      get isStart(): boolean {
        return !!self.nav?.isStart;
      },
      get isFinal(): boolean {
        return !!self.nav?.isFinal;
      },
      get isLastInSurvey(): boolean {
        return this.currentNode?.isLastInSurvey || false;
      },
      get current(): string | undefined {
        return self.nav?.current;
      },
      get currentNode() {
        return this.current
          ? self.tree?.findNodeByKey(this.current)
          : undefined;
      },
      get path(): ITreeNode[] | undefined  {
        return self.nav?.path.map(key => self.tree?.findNodeByKey(key)).filter(node => !!node) as ITreeNode[];
      },
      get next(): string | undefined {
        if (!self.nav) {
          console.log('TreeNav.next: nav is undefined');
          return undefined;
        }
        // if there are no choices to make then return default next
        const nextEdges = self.tree?.getEdgesFrom(self.nav.current);
        const hasNoChoice = nextEdges?.every(edge => edge.default);
        if (hasNoChoice) {
          return nextEdges?.[0]?.next?.key;
        }
        // return next based on user's current choice
        console.log('TreeNav.next: self.choices', self.choices.pending?.questionKey);
        const questionKey = self.choices.pending?.questionKey;
        const answerKey = self.choices.pending?.answers[0]?.key;
        if (!questionKey) {
          console.log('no next key because choices.questionKey is undefined', self.choices);
          return undefined;
        }
        // Check if the dynamically-resolved answer has a nextKey (nextQuestionKey from API).
        // If so, and the tree has a valid edge to that destination, use it.
        // This allows dynq answers to override the default tree navigation.
        const selectedAnswer = self.content.question?.answers?.find(
          (a: any) => a.key === answerKey
        );
        if (selectedAnswer?.nextKey) {
          // the answer names the next question; the node asking it may carry
          // a prefixed key (block grafted into a session)
          const edge = nextEdges?.find(e => e.next.key === selectedAnswer.nextKey || e.next.questionKey === selectedAnswer.nextKey);
          if (edge) {
            return edge.next.key;
          }
        }
        // edges start at the current node: its key, not the question key
        // (a block grafted into a session prefixes its node keys)
        const next = self.tree?.findNextFrom(self.nav.current, answerKey)?.key;
        const defaultNext = self.tree?.findDefaultNextFrom(self.nav.current)?.key;
        return next || defaultNext;
      },
      get canContinue(): boolean | undefined {
        // exercise and activity steps require an explicit accept/decline choice
        if (self.content.exercise || self.content.stepType === 'ACTIVITY') {
          return !!self.choices.exercise.isValid;
        }
        return !self.content.question || !!self.choices.pending?.valid;
      },
      get previous(): string | undefined {
        return self.nav
          ? self.tree?.getPreviousNode(self.nav?.current)?.key
          : undefined;
      },
      get stepNumber(): number {
        return self.nav?.path.length || 0;
      },
      get maxSteps(): number {
        return self.tree?.maxSteps || 0;
      },
      get stepCount(): number {
      // TODO use longest path in tree
        return self.tree?.nodes.length || 0;
      },
      get stepBackDisabled(): boolean {
        return !!self.tree?.nodes?.find(node => node.key === self.nav?.current)?.disableStepBack;
      },
    };
  });

export type ITreeNav = Instance<typeof TreeNav>;

